import { Alert, Card, CardBody } from "@heroui/react";
import { useTranslation } from "react-i18next";
import type { TSignaturePosition } from "@shared/entities";

type Props = {
  signaturePosition: TSignaturePosition;
  onPositionChange: (pos: TSignaturePosition) => void;
  error: string | null;
};

// All positions EXCEPT "none" — used for the position-picker grid. The "none"
// case is handled by the toggle card above, not as a position option.
type PlacedSignaturePosition = Exclude<TSignaturePosition, "none">;
const POSITIONS: PlacedSignaturePosition[] = [
  "top-right",
  "top-left",
  "bottom-right",
  "bottom-left",
];

export function TemplateStep3({ signaturePosition, onPositionChange, error }: Props) {
  const { t } = useTranslation("doctorTemplates");
  const noSignature = signaturePosition === "none";

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm font-medium text-default-700">{t("signatureLabel")}</p>

      {/* No signature toggle */}
      <Card
        isPressable
        onPress={() => onPositionChange(noSignature ? "bottom-right" : "none")}
        className={noSignature ? "border-2 border-primary" : "border-2 border-transparent"}
      >
        <CardBody className="flex items-center justify-center py-4">
          <span className="text-sm font-medium">{t("noSignature")}</span>
        </CardBody>
      </Card>

      {/* Position grid — only shown when signature is enabled */}
      {!noSignature && (
        <>
          <p className="text-sm text-default-500">{t("signaturePosition")}</p>
          <div className="grid grid-cols-2 gap-3">
            {POSITIONS.map((pos) => {
              const isSelected = signaturePosition === pos;
              return (
                <Card
                  key={pos}
                  isPressable
                  onPress={() => onPositionChange(pos)}
                  className={isSelected ? "border-2 border-primary" : "border-2 border-transparent"}
                >
                  <CardBody className="flex items-center justify-center py-4">
                    <span className="text-sm font-medium">
                      {t(`signaturePositions.${pos}` as `signaturePositions.${PlacedSignaturePosition}`)}
                    </span>
                  </CardBody>
                </Card>
              );
            })}
          </div>
        </>
      )}


      {error !== null && <Alert color="danger" description={error} />}
    </div>
  );
}
